import { useState } from 'react'
import { ApiError } from '../../api/client'
import type { AiLab } from '../../api/settingsAiAgent'

/**
 * Bucatile pe care le folosesc mai multe file din ecranul AI Agent.
 *
 * Cea mai importanta e perechea Lab -> Model: apare la fiecare treapta, la
 * treapta noua si la firma, si se poarta la fel in toate trei - lista adusa la
 * Connect, sau un camp liber pentru laboratoarele care nu-si listeaza modelele.
 */

/**
 * TOT ce e global din pagina, intr-un singur loc.
 *
 * Save la Global aduna comutatoarele din fila Agent Models, cheia si modelul de
 * rezerva din AI Labs & Models si implicitele din Companies - oricare din cele
 * trei butoane ar fi apasat: serverul rescrie randul intreg, deci un salvat pe
 * jumatate ar sterge cealalta jumatate. Aceeasi regula ca in macheta veche.
 */
export type GlobalDraft = {
  enabled: boolean
  /** Treapta implicita (radio-ul din tabelul de trepte). */
  effort: string
  maxSteps: number
  dailyBudget: number
  fallbackLab: string
  fallbackBaseUrl: string
  fallbackModel: string
  /** Scrisa acum; goala inseamna "nu o schimba". */
  apiKey: string
  brainUrl: string
  brainToken: string
}

export function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/** `$shortNum` din macheta: 11.8M in loc de 11 823 456. */
export function shortNum(value: number): string {
  const n = Number(value || 0)
  if (n >= 1_000_000) return trimZero(n / 1_000_000) + 'M'
  if (n >= 1000) return trimZero(n / 1000) + 'k'
  return String(n)
}

function trimZero(n: number): string {
  return String(Math.round(n * 10) / 10)
}

export function num(value: unknown): string {
  return Number(value || 0).toLocaleString('en-US')
}

/**
 * Ora, scurta si citibila - ca in `ai_audit.js`: ziua si minutul, fara secunde
 * si fara anul cand e anul asta. Intr-un jurnal se compara randuri intre ele,
 * nu cu anul.
 */
export function when(raw: unknown): string {
  const text = String(raw ?? '').replace('T', ' ')
  if (text.length < 16) return text
  return text.slice(0, 4) === String(new Date().getFullYear()) ? text.slice(5, 16) : text.slice(0, 16)
}

export function shortText(text: unknown, max: number): string {
  const value = String(text ?? '')
  return value.length > max ? value.slice(0, max) + '…' : value
}

/** Insigna unei file. `null` = tabela nu e in baza, si atunci scrie "?". */
export function Badge({ value, count }: { value: number | null; count?: boolean }) {
  if (value === null || value === undefined) {
    return (
      <span className="aia-badge aia-badge-unknown" title="This table is not in the database yet">
        ?
      </span>
    )
  }

  return <span className={count ? 'aia-badge aia-count' : 'aia-badge'}>{num(value)}</span>
}

/** Un mesaj scurt langa butonul care tocmai a fost apasat. */
export type Note = { text: string; bad?: boolean }

export function NoteText({ note }: { note: Note | null }) {
  if (!note) return null

  return <span className="aia-help" style={{ color: note.bad ? '#b42318' : '#2e7d32' }}>{note.text}</span>
}

/**
 * Perechea Lab -> Model.
 *
 * Modelul: un dropdown cu lista adusa la Connect, si un camp liber pentru
 * laboratoarele fara lista (un vLLM pornit pe un singur model, un proxy) sau
 * cand omul vrea un id care nu e in lista. Amandoua poarta aceeasi valoare; se
 * citeste cel vizibil, exact ca `modelValue()` din macheta veche.
 */
export function LabModelPick({
  labs,
  labId,
  model,
  onChange,
}: {
  labs: AiLab[]
  labId: number
  model: string
  onChange(next: { labId: number; model: string }): void
}) {
  const lab = labs.find((l) => l.id === labId) ?? null
  const models = lab?.models ?? []
  const [typed, setTyped] = useState(false)
  /* Fara lista, campul liber e singurul drum - nu se mai poate comuta. */
  const free = typed || models.length === 0

  return (
    <>
      <select
        className="form-control input-sm"
        value={labId}
        onChange={(e) => onChange({ labId: Number(e.target.value), model: '' })}
      >
        <option value={0}>-- AI lab --</option>
        {labs.map((l) => (
          <option key={l.id} value={l.id} disabled={!l.enabled}>
            {l.name}
          </option>
        ))}
      </select>
      <div className="aia-model-pick">
        {free ? (
          <input
            type="text"
            className="form-control input-sm"
            value={model}
            placeholder={
              lab && lab.status === 'connected' ? 'type the model id' : 'connect the lab first'
            }
            onChange={(e) => onChange({ labId, model: e.target.value })}
          />
        ) : (
          <select
            className="form-control input-sm"
            value={model}
            onChange={(e) => onChange({ labId, model: e.target.value })}
          >
            <option value="">-- model --</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.id}
              </option>
            ))}
            {model !== '' && !models.some((m) => m.id === model) ? (
              /* Un id scris de mana, care nu e in lista: ramane, ca sa nu se
                 piarda la un Save. */
              <option value={model}>{model} (typed)</option>
            ) : null}
          </select>
        )}
        {models.length > 0 ? (
          <a
            href="#"
            className="aia-model-toggle"
            title="type a model id instead of picking one"
            onClick={(e) => {
              e.preventDefault()
              setTyped(!typed)
            }}
          >
            {typed ? 'pick' : 'type'}
          </a>
        ) : null}
      </div>
    </>
  )
}

/**
 * Fast mode se poate bifa doar pe un model care il are - aceeasi lista ca in
 * `Config::supportsFast()` si in `DirectBrain::fastMode()`.
 */
export function supportsFast(provider: string, model: string): boolean {
  return provider === 'anthropic' && /^claude-(opus-5|opus-4-8)/.test(model)
}

/**
 * Steagul tarii, din codul de doua litere: A -> U+1F1E6. Fara imagini si fara
 * font special - doua semne Unicode pe care le deseneaza sistemul.
 */
export function flagOf(code: string): string {
  const value = String(code ?? '').trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(value)) return ''

  return [...value].map((letter) => String.fromCodePoint(0x1f1e6 + letter.charCodeAt(0) - 65)).join('')
}
